import type { Step } from "ui";

import type { BaseDataFlyoutTarget } from "fond/redux/baseDataFlyout";

import { BaseDataOptionItem, getBaseDataOptions, getBaseDataOptionsHeading } from "./flyout/options";
import type { TaskPhase } from "./flyout/TaskProgress";
import { ADD_BOUNDARY_FIRST } from "./copy";
import { BaseDataGating, Gate, ModifyDataAction } from "./gating";
import {
  BASE_DATA_LAYER_KEYS,
  BASE_DATA_LAYERS,
  BaseDataLayerCounts,
  BaseDataLayerKey,
  BaseDataMissingLayers,
  BaseDataPlannerLayer,
  BaseDataPresence,
  formatCount,
  GenerationKey,
  getBaseDataPlannerLayers,
  isGenerationKey,
  MODIFY_DATA_KEYS,
  REQUIRED_INPUT_KEYS,
} from "./layers";
import { GENERATION_NOUNS } from "./progress";
import { BaseDataRowStatus, getBaseDataRowState } from "./rowState";
import { isGeneratedLayerStale, Staleness } from "./staleness";
import { formatStepLabel, getFailedStep, getGenerationKeySteps, getGenerationPhase } from "./taskPhases";
import { BaseDataTask, BaseDataTasks, getBaseDataGenerationTasks, getBaseDataRowTasks } from "./tasks";

export type StatusLineStatus = "done" | "missing" | "required" | "optional";

/** A line of a base data row's status card. */
export type StatusLine = { key: string; status: StatusLineStatus; text: string; subtitle?: string };

/** Where the flyout footer's Next leads from a base data row, and why it can't yet. */
export type BaseDataRowNext = { target: BaseDataFlyoutTarget | null; blockedReason: string | null };

/** A base data row's generation workflow: whether it can run, and its progress or failure. */
export type BaseDataRowGeneration = {
  key: GenerationKey;
  /** Noun for what it generates, e.g. "underground network". */
  noun: string;
  gate: Gate;
  cancel: Gate;
  generatedFrom: string;
  /** The running generation's phase and steps, or null when it isn't running. */
  progress: { phase: TaskPhase; steps: Step[] } | null;
  /** Why its latest run failed, or null unless that failure still applies (see `isGenerationFailed`). */
  failure: { reason: string } | null;
};

export type BaseDataRowModel = {
  key: BaseDataLayerKey;
  status: BaseDataRowStatus;
  caption: string;
  /** Whether the row has data in any of its planner layers, or a boundary. */
  hasData: boolean;
  layers: BaseDataPlannerLayer[];
  /** Feature count shown on the row, or null for none. */
  count: number | null;
  /** Lines of the row's status card. */
  lines: StatusLine[];
  /** The boundary's area, e.g. "1.4 km²"; null without a boundary or on other rows. */
  boundaryArea: string | null;
  /** The task started from the row's flyout. */
  task: BaseDataTask | null;
  options: BaseDataOptionItem[];
  /** Heading above the row's options. */
  heading: string;
  /** Whether the row's data can change now (see `BaseDataGating.rows`). */
  change: Gate;
  delete: Gate;
  next: BaseDataRowNext;
  generation: BaseDataRowGeneration | null;
};

export type BaseDataPanelModel = {
  /** Whether the base data is ready for design. */
  isComplete: boolean;
  /** Whether generated layers are out of date while nothing generates. */
  isStale: boolean;
  /** Whether base data is being generated, or a generation run is starting. */
  isGenerating: boolean;
  modifyData: {
    /** Whether Modify data can be opened: it has a layer with data to work on and the user can edit. */
    available: boolean;
    gates: Record<ModifyDataAction, Gate>;
  };
};

export type BaseDataModelState = {
  rows: Record<BaseDataLayerKey, BaseDataRowModel>;
  panel: BaseDataPanelModel;
  /** The task of each base data row whose flyout started one, keyed by that row. */
  tasks: BaseDataTasks;
};

export type BaseDataModelInput = {
  present: BaseDataPresence;
  hasData: BaseDataPresence;
  missing: BaseDataMissingLayers;
  staleness: Staleness;
  gating: BaseDataGating;
  /** Generation workflows currently starting or in progress. */
  running: readonly GenerationKey[];
  /** The generation workflow whose latest run failed, if any. */
  failed: readonly GenerationKey[];
  /** Progress steps of the running and failed generation workflows. */
  progressSteps: readonly Step[];
  /** The task of each base data row whose flyout started one, keyed by that row. */
  ownedTasks: BaseDataTasks;
  counts: BaseDataLayerCounts;
  /** The boundary's area in the project's system of measurement, or null without a boundary. */
  boundaryArea: string | null;
  readOnly: boolean;
};

/** Return whether the Modify data tools can be opened: one of their layers has data and the user can edit. */
export const isModifyDataAvailable = (hasData: BaseDataPresence, readOnly: boolean): boolean =>
  !readOnly && MODIFY_DATA_KEYS.some((key) => hasData[key]);

/** Return why the guided walk-through can't move on from a base data layer yet, or null when it can. */
export const getNextBlockedReason = (key: BaseDataLayerKey, hasData: BaseDataPresence): string | null => {
  if (key === "boundary" && !hasData.boundary) return ADD_BOUNDARY_FIRST;
  if ((REQUIRED_INPUT_KEYS as readonly BaseDataLayerKey[]).includes(key) && !hasData[key]) return `Add ${BASE_DATA_LAYERS[key].noun} first`;
  // Leadins need underground path & points or aerial spans & poles, so the walk-through stops at the last of them.
  if (key === "aerial" && !hasData.ug && !hasData.aerial) return "Add underground path & points or aerial spans & poles first";
  return null;
};

/** Return the flyout after a base data layer's in panel order, ending with Modify data when it is available, or null after the last. */
export const getNextFlyoutTarget = (key: BaseDataLayerKey, modifyDataAvailable: boolean): BaseDataFlyoutTarget | null => {
  const next = BASE_DATA_LAYER_KEYS[BASE_DATA_LAYER_KEYS.indexOf(key) + 1];
  if (next) return next;
  return modifyDataAvailable ? "modifyData" : null;
};

/** Return whether a generation workflow's latest run failed and still applies: it isn't running again and its layer hasn't been added since. */
export const isGenerationFailed = (
  key: GenerationKey,
  { failed, running, present, staleness }: Pick<BaseDataModelInput, "failed" | "running" | "present" | "staleness">
): boolean => failed.includes(key) && !running.includes(key) && (!present[key] || isGeneratedLayerStale(key, staleness));

/** Return the lines of a base data row's status card: one per planner layer, or a single line for an empty row or the boundary. */
export const getStatusLines = (
  key: BaseDataLayerKey,
  hasData: boolean,
  layers: readonly BaseDataPlannerLayer[],
  boundaryArea: string | null
): StatusLine[] => {
  const { noun, required } = BASE_DATA_LAYERS[key];
  if (!hasData) {
    return [{ key, status: required ? "required" : "optional", text: `No ${noun} yet`, subtitle: required ? "Required" : "Optional" }];
  }
  if (key === "boundary") return [{ key, status: "done", text: boundaryArea ?? "" }];

  return layers.map(({ id, name, units, hasData: layerHasData, count }) => {
    if (!layerHasData) return { key: id, status: "missing", text: `No ${name} yet` };
    const text = count === null ? `${units[1].charAt(0).toUpperCase()}${units[1].slice(1)} added` : formatCount(count, units);
    return { key: id, status: "done", text };
  });
};

/** Return why a generation run failed, naming the step it failed at when known. */
const getFailureReason = (steps: readonly Step[]): string => {
  const step = getFailedStep(steps);
  return step ? `Failed during ${formatStepLabel(step.label, step)}.` : "Generation stopped before finishing.";
};

const getRowGeneration = (key: GenerationKey, input: BaseDataModelInput): BaseDataRowGeneration => {
  const { gating, running, progressSteps } = input;
  const steps = getGenerationKeySteps(progressSteps, key);
  return {
    key,
    noun: GENERATION_NOUNS[key],
    gate: gating.generate[key],
    cancel: gating.cancel,
    generatedFrom: gating.generatedFrom[key],
    progress: running.includes(key) ? { phase: getGenerationPhase(progressSteps, key), steps } : null,
    failure: isGenerationFailed(key, input) ? { reason: getFailureReason(steps) } : null,
  };
};

/** Return the state of the Input Data panel: each base data row and the panel as a whole. */
export const getBaseDataModel = (input: BaseDataModelInput): BaseDataModelState => {
  const { present, hasData, missing, staleness, gating, running, progressSteps, ownedTasks, counts, boundaryArea, readOnly } = input;
  const rowTasks: BaseDataTasks = { ...getBaseDataRowTasks(ownedTasks), ...getBaseDataGenerationTasks(running, progressSteps) };
  const modifyDataAvailable = isModifyDataAvailable(hasData, readOnly);

  const getRow = (key: BaseDataLayerKey): BaseDataRowModel => {
    const layers = getBaseDataPlannerLayers(key, missing[key], counts);
    const { status, caption } = getBaseDataRowState({
      key,
      present,
      missing,
      staleness,
      generate: gating.generate,
      generatedFrom: gating.generatedFrom,
      generationFailed: isGenerationKey(key) && isGenerationFailed(key, input),
      task: rowTasks[key],
      layers,
    });
    const task = ownedTasks[key] ?? null;
    const isBoundary = key === "boundary";
    const firstCount = layers[0]?.count ?? null;
    return {
      key,
      status,
      caption,
      hasData: hasData[key],
      layers,
      count: status === "done" && !isBoundary ? firstCount : null,
      lines: getStatusLines(key, hasData[key], layers, boundaryArea),
      boundaryArea: isBoundary ? boundaryArea : null,
      task,
      options: getBaseDataOptions(key, { hasData: hasData[key], gating, task }),
      heading: getBaseDataOptionsHeading(key, hasData[key]),
      change: gating.rows[key].change,
      delete: gating.rows[key].delete,
      next: { target: getNextFlyoutTarget(key, modifyDataAvailable), blockedReason: getNextBlockedReason(key, hasData) },
      generation: isGenerationKey(key) ? getRowGeneration(key, input) : null,
    };
  };

  return {
    rows: Object.fromEntries(BASE_DATA_LAYER_KEYS.map((key) => [key, getRow(key)])) as Record<BaseDataLayerKey, BaseDataRowModel>,
    panel: {
      isComplete: gating.isComplete,
      isStale: (staleness.ugStale || staleness.leadinsStale) && !gating.isGenerating,
      isGenerating: gating.isGenerating,
      modifyData: { available: modifyDataAvailable, gates: gating.modify },
    },
    tasks: ownedTasks,
  };
};
